import { bootstrapUserData } from '@/services';
import { consoleLog, quickLog } from '@/utils/console-log';
import { useEffect } from 'react';
import { useGlobalStore } from '../global-store';
import type { IUser } from '@/types';

/**
 * Initializes user-scoped store data when auth context is ready.
 *
 * Responsibilities:
 * - Guard by auth/config state.
 * - Load mapped bootstrap data via services layer.
 * - Commit data slices into global store.
 * - Handle notification and error paths.
 */
export const useInitializeUserStoreData = ({ user }: { user: IUser | null }) => {
  const {
    addNotification,
    fetchData,
    setCertifications,
    setCertificationsError,
    setCurrentUser,
    setCurrentUserBadges,
    setError,
    setLoading,
    setPersonas,
    setScores,
    setSimulations,
  } = useGlobalStore();

  // ================================
  // Bootstrap Lifecycle Effect
  // ================================
  // Runs whenever relevant auth/config/setter dependencies change.
  // All guard clauses are intentionally inside the async initializer so we can
  // perform side effects (such as setLoading(false)) before returning.
  useEffect(() => {
    // Cancellation flag protects against state writes after unmount or rapid
    // dependency changes while async work is still in flight.
    let isCancelled = false;

    // Encapsulates the full bootstrap flow for authenticated users.
    // Steps:
    // 1. Guard on auth/config preconditions.
    // 2. Seed loading/current-user state.
    // 3. Load bootstrap payload via services layer.
    // 4. Commit mapped slices + notification.
    // 5. Handle error paths and finalize loading.
    const initializeStore = async () => {
      quickLog.data('Initializing store...');

      // Guard: cannot bootstrap user-scoped data without an authenticated user id.
      if (!user?.uuid) {
        consoleLog.warn('No user found, skipping data load');
        setLoading(false);
        return;
      }

      // Guard: feature/config switch to suppress network activity.
      if (!fetchData) {
        quickLog.api('Fetch data flag is false, skipping API calls');
        setLoading(false);
        return;
      }

      // Prime app state for a fresh user bootstrap cycle.
      // - Loading is set before network activity starts.
      // - Prior global error state is cleared.
      // - Current user is synchronized into the store immediately.
      setLoading(true);
      setError(null);
      setCurrentUser(user);

      try {
        quickLog.auth('Loading data for user:', user.uuid);

        // Service boundary returns normalized slices for the store initializer.
        const userData = await bootstrapUserData(user.uuid);

        // If the effect has been cleaned up, do not commit stale data.
        if (isCancelled) return;

        // Diagnostic logging to verify mapped payload shapes during debugging.
        consoleLog.group('Mapped Frontend Data');
        quickLog.data('Mapped scorecards:', userData.scores);
        quickLog.data('Mapped availables:', userData.certifications);
        quickLog.data('Mapped user badges:', userData.badges);
        consoleLog.groupEnd();

        // Commit bootstrap payload into store slices used throughout the app.
        setCertifications(userData.certifications);
        setPersonas(userData.personas);
        setScores(userData.scores);
        setSimulations(userData.simulations);
        setCurrentUserBadges(userData.badges);

        // Success feedback for users so data load completion is explicit.
        addNotification({
          id: `data-load-success-${Date.now()}`,
          message: 'Your data has been loaded successfully.',
          variant: 'success',
          createdAt: Date.now(),
          isRead: false,
        });
      } catch (error: any) {
        // Global failure path catches bootstrap service errors and surfaces them
        // to both logs and app-level error state.
        console.error('Error initializing store:', error);
        setError(error instanceof Error ? error : 'Unknown error occurred');

        // Only surface certification error state for auth failures (401/403).
        // These indicate the token was missing or invalid when the bootstrap
        // fired — the user needs to re-authenticate to get their data.
        // Other errors (e.g. a transient 404 from a single endpoint in the
        // Promise.all) fall through to the global setError above and should
        // not be attributed specifically to certifications.
        const httpStatus = error?.response?.status ?? error?.status;
        const isCertificationError = httpStatus === 401 || httpStatus === 403;
        if (isCertificationError) {
          consoleLog.error('Error fetching certifications:', error);
          addNotification({
            id: `certifications-error-${Date.now()}`,
            message: 'Failed to load certifications. Please try again later.',
            variant: 'error',
            createdAt: Date.now(),
            isRead: false,
          });
          setCertificationsError({
            message: 'Failed to load certifications. Please try again later.',
            status: httpStatus || 500,
          });
        }
      } finally {
        // Loading is always cleared unless cleanup already occurred.
        // This prevents post-unmount state writes while still guaranteeing
        // proper UI recovery for all in-scope executions.
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    // Fire-and-forget pattern inside effect body.
    initializeStore();

    // Cleanup marks current run as stale so async completion cannot update state.
    return () => {
      isCancelled = true;
    };
  }, [
    addNotification,
    fetchData,
    setCertifications,
    setCertificationsError,
    setCurrentUser,
    setCurrentUserBadges,
    setError,
    setLoading,
    setPersonas,
    setScores,
    setSimulations,
    user,
    user?.uuid,
  ]);
};
